import { Button } from "@templeos/ui";
import type { ReactNode } from "react";
import { NavLink } from "react-router";
import { FKEYS, type KeyMap } from "../lib/shortcuts.js";
import { engine, useSession } from "../session.js";
import { useFnKeys } from "./hooks.js";

interface ShellProps<A extends string> {
  keys?: KeyMap<A>;
  onKey?: (a: A) => void;
  disabledKeys?: readonly A[];
  children: ReactNode;
}

/** POS frame: counter and status header, screen tabs, alerts, and the on-screen F1–F12 key bar. */
export function Shell<A extends string>({ keys, onKey, disabledKeys = [], children }: ShellProps<A>) {
  const s = useSession();
  useFnKeys(keys, (a: A) => { if (!s.locked && !disabledKeys.includes(a)) onKey?.(a); });
  const kind = s.me?.counter?.kind;
  const sells = kind !== "gate" && s.can("counters.sell");
  const { sync } = s;
  const syncText = sync.running ? "Syncing…" : sync.pending ? `${sync.pending} to sync` : "All synced";
  return (
    <div className="pos-app">
      <header className="pos-top">
        <div className="pos-brand">
          <strong>{s.profile?.name ?? "Temple POS"}</strong>
          <span>{s.me?.counter ? `${s.me.counter.code} · ${s.me.counter.name}` : "No counter"}</span>
        </div>
        <nav className="pos-nav" aria-label="Counter screens">
          {sells && kind !== "prasad" && <NavLink to="/sell">Billing</NavLink>}
          {sells && kind !== "prasad" && <NavLink to="/fast">Fast checkout</NavLink>}
          {sells && (kind === "prasad" || s.config?.items.some((i) => i.kind === "prasad")) && <NavLink to="/prasad">Prasad</NavLink>}
          {sells && <NavLink to="/history">History</NavLink>}
          {sells && <NavLink to="/shift">Shift</NavLink>}
          {s.can("counters.gate") && <NavLink to="/gate">Gate</NavLink>}
        </nav>
        <div className="pos-status" aria-live="polite">
          <span className={`pos-pill${s.reachable ? "" : " bad"}`}>{s.reachable ? "Online" : "Offline"}</span>
          <button type="button" className={`pos-pill${sync.blocked ? " bad" : sync.pending ? " warn" : ""}`} onClick={() => void engine.kick()}
            title={sync.lastError ?? "Sync now"}>{syncText}</button>
          <span className={`pos-pill${s.printerReady ? "" : " warn"}`}>{s.printerReady ? "Printer ready" : "Browser printing"}</span>
          {s.storageAtRisk && (
            <span className="pos-pill warn" title="The browser did not grant persistent storage, so it may clear offline bills when the disk is full. Install the POS as an app on this PC.">
              Storage not protected
            </span>
          )}
        </div>
        <div className="pos-user">
          <span>{s.me?.fullName}</span>
          <Button size="sm" onClick={s.lock}>Lock</Button>
        </div>
      </header>
      <div>
        {s.tamper && <div className="pos-banner danger" role="alert">{s.tamper}</div>}
        {sync.blocked && (
          <div className="pos-banner danger" role="alert">
            Sync stopped: {sync.blocked.reason} (sale {sync.blocked.clientRef}). Do not clear this terminal; call a supervisor.
          </div>
        )}
        {s.configError && (
          <div className="pos-banner warning" role="status">
            {s.configError} <Button size="sm" onClick={() => void s.reloadConfig()}>Reload</Button>
          </div>
        )}
      </div>
      <main className="pos-main" id="main">{children}</main>
      {keys && (
        <div className="pos-keys" role="toolbar" aria-label="Function keys">
          {FKEYS.map((k) => {
            const sc = keys[k];
            const off = !sc || disabledKeys.includes(sc.action);
            return (
              <button key={k} type="button" className="pos-key" aria-disabled={off || undefined} tabIndex={-1}
                onClick={() => { if (!off && sc) onKey?.(sc.action); }}>
                <kbd>{k}</kbd><span>{sc?.label ?? ""}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
